import { useId, type ReactNode } from "react";
import { Icon, type IconName } from "../icons/Icon";
import { Button } from "../primitives/Button";
import { cx } from "../utils/format";

/** The sidebar and consent round (2026-10-24; brief "Also in this round: one consent layout for every service that connects", the founder's
 *  mockup docs/design/consent-screen-mockup.png): the one page on which anything asks to connect to a workspace (a member's AI app, a WordPress
 *  site, later a GitHub account), the right half of the sign-in's split. Top to bottom: two pills (who asks → the workspace), the question as
 *  the title, the choosers (where the asker lets the person pick), room for notices and questions, "It can" (plain words; a code in the line's
 *  hover), "It hears about" (a site's events), the one "Never" line under a hairline, the amber strip with the shield (a line before the
 *  warning, such as where a sign-in goes back to, and something after it, such as the member's own tick), Cancel and the wide primary action,
 *  then "Signed in as … · Not you?" and one closing line. A screen fills it; it never builds its own lists, strip or buttons. */
export interface ConsentLine { text: string; /** a quiet addition after a middle dot ("200 a day") */ note?: string; /** the line's hover (a scope's code) */ hover?: string }
export interface ConsentPageProps {
  /** who asks: its name (in large type when it is a host the page vouches for) and its mark */
  asker: { name: string; host?: boolean; icon: IconName };
  workspace: { name: string; mark: string; logo?: string | null };
  title: string;
  choosers?: ReactNode;
  /** between the choosers and "It can": an "already connected" notice, a question, a plan's limit */
  notices?: ReactNode;
  can: ConsentLine[];
  hears?: string[];
  never?: string;
  strip: { warning: string; more?: string[]; before?: ReactNode; after?: ReactNode };
  problem?: string | null;
  actions: { primary: string; onPrimary: () => void; disabled?: boolean; onCancel: () => void; cancelDisabled?: boolean };
  signedInAs: { email: string; onNotYou: () => void };
  footnote: string;
}

export function ConsentPage(p: ConsentPageProps) {
  return (
    <div className="ob-consent">
      <ConsentPills asker={p.asker} workspace={p.workspace} />
      <h1 className="ob-consent__title">{p.title}</h1>
      {p.choosers && <div className="ob-consent__choosers">{p.choosers}</div>}
      {p.notices}
      <ConsentList label="It can" icon="check" lines={p.can} />
      {p.hears && p.hears.length > 0 && <ConsentList label="It hears about" icon="bell" lines={p.hears.map((text) => ({ text }))} />}
      {p.never && <><hr className="ob-consent__rule" /><ul className="ob-consent__list ob-consent__list--never" aria-label="Never"><li className="ob-consent__line"><Icon name="blocked" size="sm" /><span>{p.never}</span></li></ul></>}
      <ConsentStrip {...p.strip} />
      {p.problem && <p className="ob-signin__problem" role="alert">{p.problem}</p>}
      <div className="ob-consent__buttons">
        <Button variant="outline" className="ob-consent__btn" disabled={p.actions.cancelDisabled} onClick={p.actions.onCancel}>Cancel</Button>
        <Button variant="primary" className="ob-consent__btn ob-consent__btn--primary" disabled={p.actions.disabled} onClick={p.actions.onPrimary}>{p.actions.primary}</Button>
      </div>
      <p className="ob-consent__foot">Signed in as {p.signedInAs.email} · <button type="button" className="ob-link" onClick={p.signedInAs.onNotYou}>Not you?</button></p>
      <p className="ob-consent__foot">{p.footnote}</p>
    </div>
  );
}

/** who asks → the workspace */
function ConsentPills({ asker, workspace }: Pick<ConsentPageProps, "asker" | "workspace">) {
  return (
    <div className="ob-consent__pills">
      <span className="ob-consent__pill"><span className="ob-consent__pill-mark"><Icon name={asker.icon} size="sm" /></span><span className={cx("ob-consent__pill-name", asker.host && "ob-consent__host")}>{asker.name}</span></span>
      <Icon name="arrowForward" size="sm" className="ob-consent__arrow" />
      <span className="ob-consent__pill">
        <span className="ob-consent__pill-mark ob-consent__pill-mark--workspace">{workspace.logo ? <img src={workspace.logo} alt="" /> : workspace.mark}</span>
        <span className="ob-consent__pill-name">{workspace.name}</span>
      </span>
    </div>
  );
}

/** a small label and its lines, each with the same mark */
function ConsentList({ label, icon, lines }: { label: string; icon: IconName; lines: ConsentLine[] }) {
  const id = useId();
  return (
    <div className="ob-consent__group">
      <p className="ob-consent__label" id={id}>{label}</p>
      <ul className="ob-consent__list" aria-labelledby={id}>
        {lines.map((l, i) => <li key={i} className="ob-consent__line" title={l.hover}><Icon name={icon} size="sm" /><span>{l.text}{l.note && <span className="ob-consent__note"> · {l.note}</span>}</span></li>)}
      </ul>
    </div>
  );
}

/** the amber strip: what comes before the warning, the warning and its extra lines, what follows */
function ConsentStrip({ warning, more = [], before, after }: ConsentPageProps["strip"]) {
  return (
    <div className="ob-consent__strip" role="note">
      <Icon name="shield" size="sm" />
      <div className="ob-consent__strip-body">
        {before}
        <p className="ob-consent__strip-line">{warning}</p>
        {more.map((m) => <p key={m} className="ob-consent__strip-line">{m}</p>)}
        {after}
      </div>
    </div>
  );
}
